import { defineStore } from 'pinia';
import { http } from '../api/axios';
import {
  buildBrowserSuccessUrl,
  clearBrowserLoginState,
  loadBrowserLoginState,
  pollBrowserSession,
  saveBrowserLoginState,
  safeReturnPath,
} from '../auth/browser-session.mjs';

import { createAuthLifecycle, logoutLocally } from '../auth/auth-lifecycle.mjs';

let profileRequest = null;
let loginRequest = null;
const lifecycle = createAuthLifecycle();

export const useAuthStore = defineStore('auth', {
  state: () => ({
    userInfo: null,
    profileVerified: false,
  }),

  getters: {
    isAuthenticated: (state) => state.profileVerified && !!state.userInfo,
    user: (state) => state.userInfo,
    isAdmin: (state) => state.userInfo && ['admin', 'superuser'].includes(state.userInfo.role),
  },

  actions: {
    setAuth(user) {
      lifecycle.invalidate();
      this.userInfo = user || null;
      this.profileVerified = !!user;
    },

    clearAuth() {
      lifecycle.invalidate();
      this.userInfo = null;
      this.profileVerified = false;
    },

    beginBrowserLogin(returnPath = '/dashboard') {
      if (loginRequest && lifecycle.isCurrent(loginRequest.revision)) return loginRequest.promise;
      const pending = { revision: lifecycle.snapshot(), promise: null };
      loginRequest = pending;
      pending.promise = (async () => {
        try {
          try {
            const user = await this.fetchProfile();
            if (!lifecycle.isCurrent(pending.revision)) return;
            if (user) {
              window.location.replace(safeReturnPath(returnPath));
              return;
            }
          } catch (error) {
            if (!lifecycle.isCurrent(pending.revision)) return;
            if (error.response?.status !== 401) throw error;
          }
          this.clearAuth();
          pending.revision = lifecycle.snapshot();
          await http.get('/auth/csrf/');
          if (!lifecycle.isCurrent(pending.revision)) return;
          const createResponse = await http.post('/auth/browser-session/', {
            client_key: 'autosquare_relay_admin',
            success_url: buildBrowserSuccessUrl(window.location),
          });
          if (!lifecycle.isCurrent(pending.revision)) return;
          const session = createResponse.data;
          if (session.status === 'completed') {
            this.setAuth(session.user);
            window.location.replace(safeReturnPath(returnPath));
            return;
          }
          saveBrowserLoginState(window.sessionStorage, session, returnPath);
          window.location.replace(session.login_url);
        } finally {
          if (loginRequest === pending) loginRequest = null;
        }
      })();
      return pending.promise;
    },

    async completeBrowserLogin() {
      const revision = lifecycle.snapshot();
      const session = loadBrowserLoginState(window.sessionStorage);
      if (!session) {
        const user = await this.fetchProfile();
        if (!lifecycle.isCurrent(revision) || !user) throw new Error('未找到有效登录会话，请重新登录。');
        return '/dashboard';
      }
      let result;
      try {
        result = await pollBrowserSession({ api: http, session });
      } catch (error) {
        if (error.sessionExpired && lifecycle.isCurrent(revision)) clearBrowserLoginState(window.sessionStorage);
        throw error;
      }
      if (!lifecycle.isCurrent(revision)) throw new Error('登录状态已变更，请刷新页面。');
      this.setAuth(result.user);
      clearBrowserLoginState(window.sessionStorage);
      return session.return_path;
    },

    fetchProfile() {
      if (profileRequest && lifecycle.isCurrent(profileRequest.revision)) return profileRequest.promise;
      const pending = { revision: lifecycle.snapshot(), promise: null };
      profileRequest = pending;
      pending.promise = (async () => {
        try {
          const response = await http.get('/auth/profile/');
          if (!lifecycle.isCurrent(pending.revision)) return null;
          this.userInfo = response.data;
          this.profileVerified = true;
          return this.userInfo;
        } catch (error) {
          if (lifecycle.isCurrent(pending.revision)) {
            this.profileVerified = false;
            if (error.response?.status === 401) this.userInfo = null;
          }
          throw error;
        } finally {
          if (profileRequest === pending) profileRequest = null;
        }
      })();
      return pending.promise;
    },

    async logout() {
      await logoutLocally({
        lifecycle,
        request: async () => {
          await http.get('/auth/csrf/');
          await http.post('/auth/logout/');
        },
        clear: () => this.clearAuth(),
        redirect: () => { window.location.href = '/login'; },
      });
    },

    async reLogin() {
      await this.logout();
    },
  },
});
